/**
 * apps/web :: lib/languages
 *
 * The 9 MVP languages supported by the rule-diff engine + onboarding wizard.
 * Wire codes match the backend `LangCode` enum (see
 * `kmp-shared-core/src/main/kotlin/cx/humane/roadbridge/shared/profile/LangCode.kt`).
 *
 * Each entry carries:
 *   - `code`: wire-format language code (ISO 639-1 or BCP-47 script variant).
 *   - `nativeName`: display name in its own script — the primary card label.
 *   - `englishName`: transliteration / English name below in smaller type, so
 *     users who don't recognise their own script still find their language.
 *
 * Order mirrors the PRD priority: English + Spanish first (widest coverage),
 * then the four Indian-diaspora top-n (Hindi + Telugu + Tagalog), then the
 * Slavic locales, then Turkish (Wave 13), then Chinese (handbook validated).
 */

export type LangCode =
  | "en"
  | "es"
  | "hi"
  | "te"
  | "tl"
  | "ru"
  | "uk"
  | "tr"
  | "zh-Hans";

export interface Language {
  code: LangCode;
  nativeName: string;
  englishName: string;
}

export const LANGUAGES: readonly Language[] = [
  { code: "en", nativeName: "English", englishName: "English" },
  { code: "es", nativeName: "Español", englishName: "Spanish" },
  { code: "hi", nativeName: "हिन्दी", englishName: "Hindi" },
  { code: "te", nativeName: "తెలుగు", englishName: "Telugu" },
  { code: "tl", nativeName: "Tagalog", englishName: "Tagalog" },
  { code: "ru", nativeName: "Русский", englishName: "Russian" },
  { code: "uk", nativeName: "Українська", englishName: "Ukrainian" },
  { code: "tr", nativeName: "Türkçe", englishName: "Turkish" },
  { code: "zh-Hans", nativeName: "简体中文", englishName: "Simplified Chinese" },
] as const;

/** Look up a language by wire code. Returns `undefined` for unknown codes. */
export function getLanguage(code: string): Language | undefined {
  return LANGUAGES.find((l) => l.code === code);
}

/**
 * Map a wire-format `LangCode` to the i18next locale key actually shipped
 * in the web client. Returns `null` for codes that don't yet have a UI
 * bundle (telugu, russian, ukrainian as of ROADS-508). Callers that
 * surface a language picker can still let users pick those codes for
 * their *content* preference; the UI just stays in English until the
 * bundle ships.
 *
 * The interesting case: `zh-Hans` (the LangCode wire form, matching the
 * shared Kotlin enum) maps to `zh-CN` (the i18next key on disk). Every
 * other supported code is identity.
 */
export function langCodeToI18nLocale(code: LangCode): string | null {
  switch (code) {
    case "en":
    case "es":
    case "hi":
    case "tl":
    case "tr":
      return code;
    case "zh-Hans":
      return "zh-CN";
    case "te":
    case "ru":
    case "uk":
      return null;
  }
}

/**
 * Inline display label for a language entry.
 *
 * For most locales the native name + English name carry different value
 * (`हिन्दी · Hindi`, `简体中文 · Simplified Chinese`) — readers who can't
 * read the native script can still locate their language by the English
 * label. But when the two strings are identical (`English` / `English`,
 * `Tagalog` / `Tagalog`), repeating the same word reads as a typo. This
 * helper collapses the redundant pair to a single label (ROADS-507).
 *
 * The separator is intentionally `·` (middle dot) to match the existing
 * dropdown / review-step convention.
 */
export function formatLanguageLabel(language: Pick<Language, "nativeName" | "englishName">): string {
  return language.nativeName === language.englishName
    ? language.nativeName
    : `${language.nativeName} · ${language.englishName}`;
}
